import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import { useEffect, useRef } from "react";
import { StoreProvider } from "@/lib/store";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import HomePage from "@/pages/HomePage";
import CatalogPage from "@/pages/CatalogPage";
import ProductPage from "@/pages/ProductPage";
import FavoritesPage from "@/pages/FavoritesPage";
import RulesPage from "@/pages/RulesPage";
import AboutPage from "@/pages/AboutPage";
import AdminPage from "@/pages/AdminPage";
import AccountPage from "@/pages/AccountPage";
import SupportPage from "@/pages/SupportPage";

// Smart ScrollToTop: only scroll to top for fresh page navigations
function ScrollToTop() {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  const prevPath = useRef(pathname);

  useEffect(() => {
    const prev = prevPath.current;
    prevPath.current = pathname;

    // Skip scroll for POP (back/forward) to catalog from product
    if (pathname.startsWith("/catalog") && prev.startsWith("/product/") && navType === "POP") {
      return;
    }

    // Skip scroll for REPLACE within catalog (category tab switching)
    if (pathname.startsWith("/catalog") && prev.startsWith("/catalog") && navType === "REPLACE") {
      return;
    }

    window.scrollTo(0, 0);
  }, [pathname, navType]);

  return null;
}

function AppRoutes({ adminPath }) {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/catalog" element={<CatalogPage />} />
        <Route path="/catalog/:category" element={<CatalogPage />} />
        <Route path="/product/:id" element={<ProductPage />} />
        <Route path="/favorites" element={<FavoritesPage />} />
        <Route path="/rules" element={<RulesPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/support" element={<SupportPage />} />
      </Route>
      {/* Dynamic admin route from backend config */}
      <Route path={`/${adminPath}`} element={<AdminPage />} />
    </Routes>
  );
}

function App() {
  // Admin path from env or fallback — no need to expose via public API
  const adminPath = process.env.REACT_APP_ADMIN_PATH || "sz-manage-84";

  return (
    <StoreProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AppRoutes adminPath={adminPath} />
      </BrowserRouter>
      <Toaster position="bottom-center" theme="dark" />
    </StoreProvider>
  );
}

export default App;
